/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { debounce, find, isEmpty, isEqual, max, min, remove, sortBy, sumBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import createEngine, {
  DagreEngine,
  DiagramEngine,
  DiagramModel,
  NodeModel,
  PortModelAlignment,
} from '@projectstorm/react-diagrams';
import { CanvasWidget, InputType, ZoomCanvasAction } from '@projectstorm/react-canvas-core';
import { LinkModel } from '@projectstorm/react-diagrams-core';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { Device, Draft } from '@/store/Station/constants';
import { useStores } from '@/hooks/use-stores';
import logger from '@/utils/logger';
import { SpinCentered } from '@/comp';
import { EPButton } from '@/components';
import { CustomThemeToken } from '@/theme';
import { usePreviousValue } from '@/hooks/usePreviousValue';

import { createLoadingProgressTimer, deviceIsOfType } from './utils/utils';
import NoFlowControlNotificationModal from './components/NoFlowControlNotificationModal';
import { PortFactory } from './ports/PortFactory';
import { FourPortPipeModel } from './ports/FourPortPipeModel';
import { PipeLinkFactory, PipeLinkModel } from './links/PipeLink';
import { BoosterNodeFactory } from './nodes/booster/BoosterNodeFactory';
import { BoosterNodeModel } from './nodes/booster/BoosterNodeModel';
import { CompressorNodeFactory } from './nodes/compressor/CompressorNodeFactory';
import { CompressorNodeModel } from './nodes/compressor/CompressorNodeModel';
import { DesiccantAirDryerNodeModel } from './nodes/desiccantAirDryer/DesiccantAirDryerNodeModel';
import { DesiccantAirDryerNodeFactory } from './nodes/desiccantAirDryer/DesiccantAirDryerNodeFactory';
import { FlowSensorNodeModel } from './nodes/flowSensor/FlowSensorNodeModel';
import { FlowSensorNodeFactory } from './nodes/flowSensor/FlowSensorNodeFactory';
import { PressureSensorNodeModel } from './nodes/pressureSensor/PressureSensorNodeModel';
import { WaterPressureSensorNodeModel } from '@/comp/MapBuilder/nodes/waterPressureSensor/WaterPressureSensorNodeModel';
import { PressureSensorNodeFactory } from './nodes/pressureSensor/PressureSensorNodeFactory';
import { WaterPressureSensorNodeFactory } from '@/comp/MapBuilder/nodes/waterPressureSensor/WaterPressureSensorNodeFactory';
import { DewpointSensorNodeModel } from './nodes/dewpointSensor/DewpointSensorNodeModel';
import { DewpointSensorFactory } from './nodes/dewpointSensor/DewpointSensorFactory';
import { FlowAndPressureSensorNodeFactory } from './nodes/flowAndPressureSensor/FlowAndPressureSensorNodeFactory';
import { FlowAndPressureSensorNodeModel } from './nodes/flowAndPressureSensor/FlowAndPressureSensorNodeModel';
import { RefrigeratedAirDryerNodeModel } from './nodes/refrigeratedAirDryer/RefrigeratedAirDryerNodeModel';
import { AirDryerRefrigeratedNodeFactory } from './nodes/refrigeratedAirDryer/RefrigeratedAirDryerNodeFactory';
import { AirFilterNodeModel } from './nodes/airFilter/AirFilterNodeModel';
import { AirFilterNodeFactory } from './nodes/airFilter/AirFilterNodeFactory';
import { ProductionFloorNodeFactory } from './nodes/productionFloor/ProductionFloorNodeFactory';
import { ProductionFloorNodeModel } from './nodes/productionFloor/ProductionFloorNodeModel';
import { ValveNodeFactory } from './nodes/valve/ValveNodeFactory';
import { ValveNodeModel } from './nodes/valve/ValveNodeModel';
import { AirTemperatureSensorFactory } from './nodes/airTemperatureSensor/AirTemperatureSensorFactory';
import { AirTemperatureSensorNodeModel } from './nodes/airTemperatureSensor/AirTemperatureSensorNodeModel';
import { DewpointAndPressureSensorNodeFactory } from './nodes/dewpointAndPressureSensor/DewpointAndPressureSensorNodeFactory';
import { DewpointAndPressureSensorNodeModel } from './nodes/dewpointAndPressureSensor/DewpointAndPressureSensorNodeModel';
import { DummyNodeModel } from './nodes/dummyNode/DummyNodeModel';
import { DummyNodeFactory } from './nodes/dummyNode/DummyNodeFactory';
import { InfluentFlowSensorNodeFactory } from './nodes/influentFlowSensor/InfluentFlowSensorNodeFactory';
import { InfluentFlowSensorNodeModel } from './nodes/influentFlowSensor/InfluentFlowSensorNodeModel';
import { DissolvedOxygenSensorNodeFactory } from './nodes/dissolvedOxygenSensor/DissolvedOxygenSensorNodeFactory';
import { DissolvedOxygenSensorNodeModel } from './nodes/dissolvedOxygenSensor/DissolvedOxygenSensorNodeModel';
import { AmmoniaAmmoniumSensorNodeFactory } from './nodes/ammoniaAmmoniumSensor/AmmoniaAmmoniumSensorNodeFactory';
import { AmmoniaAmmoniumSensorNodeModel } from './nodes/ammoniaAmmoniumSensor/AmmoniaAmmoniumSensorNodeModel';
import { NitrateSensorNodeFactory } from './nodes/nitrateSensor/NitrateSensorNodeFactory';
import { NitrateSensorNodeModel } from './nodes/nitrateSensor/NitrateSensorNodeModel';
import { NitriteSensorNodeFactory } from './nodes/nitriteSensor/NitriteSensorNodeFactory';
import { NitriteSensorNodeModel } from './nodes/nitriteSensor/NitriteSensorNodeModel';
import { OxidationReductionPotentialSensorNodeFactory } from './nodes/oxidationReductionPotentialSensor/OxidationReductionPotentialSensorNodeFactory';
import { OxidationReductionPotentialSensorNodeModel } from './nodes/oxidationReductionPotentialSensor/OxidationReductionPotentialSensorNodeModel';
import { PHSensorNodeFactory } from './nodes/phSensor/PHSensorNodeFactory';
import { PHSensorNodeModel } from './nodes/phSensor/PHSensorNodeModel';
import { BlowerNodeModel } from './nodes/blower/BlowerNodeModel';
import { BlowerNodeFactory } from './nodes/blower/BlowerNodeFactory';
import { AerationBasinNodeFactory } from './nodes/aerationBasin/AerationBasinNodeFactory';
import { AerationBasinNodeModel } from './nodes/aerationBasin/AerationBasinNodeModel';
import { AirTankNodeModel } from './nodes/airTank/AirTankNodeModel';
import { NodeModelTypeEnum } from './nodes/enums';
import { AirTankNodeFactory } from './nodes/airTank/AirTankNodeFactory';
import { permissions } from '@/constants';
import EditModeMenu from './components/PIDEditing/EditModeMenu';
import PIDEditStore, { MenuType } from '@/store/PIDEditing/PIDEditStore';
import { Container, EditMapContainer, LoadingOverlay } from './Mapbuilder.styles';
import { ProactiveEngineStateEnum } from '@/constants/enums';
import PIDFullscreenEditor from './components/PIDEditing/PIDFullscreenEditor/PIDFullscreenEditor';
import StationStore from '@/store/Station/StationStore';
import MapBuilderStore from '@/store/MapBuilderStore';
import PermissionStore from '@/store/PermissionStore';
import UserStore from '@/store/User/UserStore';
import ProactiveEngineStore from '@/store/ProactiveEngineStore';
import { StationControlType } from '@/store/Lifecycle/constants';
import { useDeviceIdFromUrl } from '@/hooks/useDeviceIdFromUrl';

let nodes = new Map();
let pipes = new Map();

const DEFAULT_OFFSET = 0;
let INITIAL_OFFSET_X = null;
let INITIAL_OFFSET_Y = null;

const HIDDEN_COMPONENT_NAME = '$HIDDEN$';

const { useToken } = theme;
interface ExtendedNodeModel extends NodeModel {
  device: Device;
}

const distribute = (dagreEngine: DagreEngine, engine: DiagramEngine) => {
  dagreEngine.redistribute(engine.getModel());
  updatePositions(engine);
  engine.repaintCanvas();
};

const updatePositions = (engine: DiagramEngine) => {
  const nodesArr = Array.from(nodes.values());
  const compressorsArr = nodesArr.filter(node => compressorTypes.includes(node.getEntity().deviceType));
  const compressorArrSorted = compressorsArr.sort((a, b) => {
    if (a.device.elementIndex !== b.device.elementIndex) return a.device.elementIndex - b.device.elementIndex;
    if (a.getX() === b.getX()) {
      return a.getY() - b.getY();
    }
    return a.getX() - b.getX();
  });
  const [minCompressor, ...restCompressors] = compressorArrSorted;
  const alignedCompressors = [minCompressor];
  const distanceBetweenCompressors = compressorArrSorted.length > 3 ? 20 : 40;

  //Align compressors to the left
  restCompressors.forEach(compressor => {
    const alignedBoundingBox = engine.getBoundingNodesRect(alignedCompressors);
    const alignedCompressorsMedianY = alignedBoundingBox.getLeftMiddle();
    const compressorMedianY = compressor.getBoundingBox().getLeftMiddle();
    const isCompressorAboveMedian = compressorMedianY < alignedCompressorsMedianY;

    isCompressorAboveMedian
      ? compressor.setPosition(
          minCompressor.getX(),
          alignedBoundingBox.getTopLeft().y - compressor.height - distanceBetweenCompressors,
        )
      : compressor.setPosition(minCompressor.getX(), alignedBoundingBox.getBottomLeft().y + distanceBetweenCompressors);

    alignedCompressors.push(compressor);
  });
  const otherNodesArr = nodesArr.filter(node => !compressorTypes.includes(node.getEntity().deviceType));
  const otherNodesArrSorted = sortBy(otherNodesArr, o => o.getPosition().x);
  otherNodesArrSorted.forEach(node => {
    const inLinks: LinkModel[] = Object.values(node.ports.left.links);
    const fromDeviceFirst = inLinks[0]?.getSourcePort().getParent();
    const toDevices = Object.values(fromDeviceFirst.getPorts().right.links).map(link => link.getTargetPort());
    const nodesBoundingBoxMinY = min(inLinks.map(link => link.getSourcePort().getY()));
    const nodesBoundingBoxMaxY = max(inLinks.map(link => link.getSourcePort()).map(port => port.getY() + port.height));
    const nodesBoundingBoxMedianY = (nodesBoundingBoxMinY + nodesBoundingBoxMaxY) / 2;
    if (toDevices.length === 1) {
      node.setPosition(node.getX(), nodesBoundingBoxMedianY - node.height / 2);
    } else {
      const toDevicesOrdered = sortBy(toDevices, o => o.getY());
      const spaceHeight = toDevicesOrdered.length > 6 ? 150 : 220;
      const totalHeight = sumBy(toDevicesOrdered, o => o.height) + (toDevicesOrdered.length - 1) * spaceHeight;
      let relativePositionY = 0;
      for (let i = 0; i < toDevicesOrdered.length; i++) {
        toDevicesOrdered[i]
          .getParent()
          .setPosition(
            toDevicesOrdered[i].getParent().getX(),
            nodesBoundingBoxMedianY - totalHeight / 2 + relativePositionY - toDevicesOrdered[i].getParent().height / 2,
          );
        relativePositionY += toDevicesOrdered[i].height;
        relativePositionY += spaceHeight;
      }
    }
  });

  const graphSize = engine.getBoundingNodesRect(engine.getModel().getNodes());
  nodes.forEach(node => node.setPosition(node.getX(), node.getY() - graphSize.getTopLeft().y));
};

const verticalPipeSorting = devices => {
  const pipesArray = remove(devices, { deviceType: 'pipe' });
  const sortedPipesArray = sortBy(pipesArray, (pipe: { deviceId: string; elementIndex: number }) => {
    const fromDevice = devices.find(device => {
      return device.toDevices?.includes(pipe.deviceId) && compressorTypes.includes(device.deviceType);
    });
    return fromDevice?.elementIndex ?? pipe.elementIndex;
  });
  devices.push(...sortedPipesArray);
};

interface MapBuilderProps {
  devicesSource: Draft[];
  windowIsOnFocus?: boolean;
  isMainMap?: boolean;
  isEditMode?: boolean;
}

const MapBuilder = ({ windowIsOnFocus, devicesSource, isMainMap, isEditMode = false }: MapBuilderProps) => {
  const [diagramCoverOpacity, setDiagramCoverOpacity] = useState(0);
  const [loadingScreenProgress, setLoadingScreenProgress] = useState(0);
  const [isNoFlowControlNotificationModalOpen, setIsNoFlowControlNotificationModalOpen] = useState(false);
  const { token } = useToken() as unknown as CustomThemeToken;
  const previousToken = usePreviousValue(token);
  const resetMapTimersRef = useRef<{ progressTimer?: NodeJS.Timeout; timeout?: NodeJS.Timeout }>({});

  const {
    stationStore,
    mapBuilderStore,
    permissionStore,
    pidEditStore,
    userStore,
    proactiveEngineStore,
  }: {
    stationStore?: StationStore;
    mapBuilderStore?: MapBuilderStore;
    permissionStore?: PermissionStore;
    pidEditStore?: PIDEditStore;
    userStore?: UserStore;
    proactiveEngineStore?: ProactiveEngineStore;
  } = useStores();
  const { t } = useTranslation();
  const devices = Object.assign([], devicesSource) ?? [];

  // Helper functions for modal localStorage management
  const getModalStorageKey = (stationId: string) => `noFlowControlModalShown_${stationId}`;
  const hasModalBeenShown = (stationId: string) => !!localStorage.getItem(getModalStorageKey(stationId));
  const markModalAsShown = (stationId: string) => localStorage.setItem(getModalStorageKey(stationId), 'true');

  const handleAddDeviceClick = useCallback(
    (_pipeId: string, screenPosition: { x: number; y: number }) => {
      pidEditStore.showMenu(MenuType.AddDevice, screenPosition);
    },
    [pidEditStore],
  );

  useEffect(() => {
    if (!stationStore.isSidebarTransitioning) {
      resetZoom();
    }
  }, [stationStore.isSidebarTransitioning]);

  // Show popup after page is loaded, but only once per station
  useEffect(() => {
    if (
      !stationStore.isLoading &&
      !stationStore.isLoadingDraftPid &&
      !pidEditStore.isLoading &&
      !pidEditStore.isLoadingDraft &&
      stationStore.currentStation?.id
    ) {
      if (!hasModalBeenShown(stationStore.currentStation.id) && displayNoFlowControlNotificationPopup()) {
        setIsNoFlowControlNotificationModalOpen(true);
      }
    }
  }, [
    stationStore.isLoading,
    stationStore.isLoadingDraftPid,
    pidEditStore.isLoading,
    pidEditStore.isLoadingDraft,
    stationStore.currentStation?.id,
  ]);

  verticalPipeSorting(devices);
  const [isInit, setIsInit] = useState(false);
  const [isResetZoomVisible, setIsResetZoomVisible] = useState(false);
  useDeviceIdFromUrl(isInit, devices.length);
  const [engine] = useState(() => {
    const newEngine = createEngine({ registerDefaultZoomCanvasAction: false });
    newEngine.getActionEventBus().registerAction(new ZoomCanvasAction({ inverseZoom: true }));
    const newModel = new DiagramModel();
    newModel.registerListener({
      eventDidFire: eventDidFireHandler,
    });
    newEngine.setModel(newModel);

    newEngine
      .getPortFactories()
      .registerFactory(new PortFactory('pipe', new FourPortPipeModel(PortModelAlignment.LEFT)) as any);
    newEngine.getNodeFactories().registerFactory(new CompressorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new ValveNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new BoosterNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new DesiccantAirDryerNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new FlowSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new DewpointSensorFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new AirTemperatureSensorFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new PressureSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new WaterPressureSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new AirDryerRefrigeratedNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new FlowAndPressureSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new DewpointAndPressureSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new AirFilterNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new ProductionFloorNodeFactory(isMainMap) as any);
    newEngine
      .getNodeFactories()
      .registerFactory(new AirTankNodeFactory(isMainMap, NodeModelTypeEnum.WetAirTank) as any);
    newEngine
      .getNodeFactories()
      .registerFactory(new AirTankNodeFactory(isMainMap, NodeModelTypeEnum.DryAirTank) as any);
    newEngine.getNodeFactories().registerFactory(new DummyNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new InfluentFlowSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new DissolvedOxygenSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new PHSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new AmmoniaAmmoniumSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new NitrateSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new NitriteSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new OxidationReductionPotentialSensorNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new BlowerNodeFactory(isMainMap) as any);
    newEngine.getNodeFactories().registerFactory(new AerationBasinNodeFactory(isMainMap) as any);
    newEngine
      .getLinkFactories()
      .registerFactory(new PipeLinkFactory(isEditMode, handleAddDeviceClick, pidEditStore) as any);

    return newEngine;
  });

  const clearClickedPipe = useCallback(() => {
    const pipeLinkFactory = engine.getLinkFactories().getFactory('pipe') as PipeLinkFactory;
    if (pipeLinkFactory) {
      pipeLinkFactory.setClickedPipe(null);
    }
  }, [engine]);

  const clearClickedPipeRef = useRef(clearClickedPipe);
  clearClickedPipeRef.current = clearClickedPipe;

  // Clear clicked pipe callback
  useEffect(() => {
    pidEditStore.setClearClickedPipeCallback(() => clearClickedPipeRef.current());
  }, []);

  // Register device type change callback
  useEffect(() => {
    const pipeLinkFactory = engine.getLinkFactories().getFactory('pipe') as PipeLinkFactory;
    if (pipeLinkFactory) {
      const notifyDeviceTypeChange = () => {
        pipeLinkFactory.notifyDeviceTypeChange();
      };
      pidEditStore.setDeviceTypeChangeCallback(notifyDeviceTypeChange);
    }
  }, [engine, pidEditStore]);

  // Subscribe to clicked pipe changes from factory
  useEffect(() => {
    const pipeLinkFactory = engine.getLinkFactories().getFactory('pipe') as PipeLinkFactory;
    if (pipeLinkFactory) {
      const unsubscribe = pipeLinkFactory.subscribeToClickedPipe(pipeId => {
        const pipe = engine.getModel().getLink(pipeId) as PipeLinkModel;
        pidEditStore.setClickedPipe(pipe);
      });

      return unsubscribe;
    }
  }, [engine, pidEditStore]);

  // Handle canvas background clicks to hide add device menu
  useEffect(() => {
    const canvas = engine.getCanvas();
    if (canvas) {
      const handleCanvasBackgroundClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target === canvas || target.tagName === 'svg') {
          pidEditStore.hideMenu();
        }
      };

      canvas.addEventListener('click', handleCanvasBackgroundClick);
      return () => canvas.removeEventListener('click', handleCanvasBackgroundClick);
    }
  }, [engine, pidEditStore.hideMenu]);

  const { offsetX: storeOffsetX, offsetY: storeOffsetY, zoom: storeZoom } = mapBuilderStore.zoomData;

  function eventDidFireHandler(e) {
    const model = engine.getModel();

    switch (e.function) {
      case 'offsetUpdated':
        offsetUpdateEventHandler(model);
        updateStoredZoomDebounced();
        break;
      case 'zoomUpdated':
        updateStoredZoomDebounced();
        break;
      default:
        break;
    }
  }

  const offsetUpdateEventHandler = newModel => {
    const offsetX = newModel.getOffsetX();
    const offsetY = newModel.getOffsetY();

    if (INITIAL_OFFSET_X === null) {
      INITIAL_OFFSET_X = offsetX;
      INITIAL_OFFSET_Y = offsetY;
    }

    (offsetX === INITIAL_OFFSET_X && offsetY === INITIAL_OFFSET_Y) || nodes.size === 0
      ? setIsResetZoomVisible(false)
      : setIsResetZoomVisible(true);
  };

  const resetMap = () => {
    // Clear any existing timers to prevent memory leaks
    if (resetMapTimersRef.current.progressTimer) {
      clearInterval(resetMapTimersRef.current.progressTimer);
    }
    if (resetMapTimersRef.current.timeout) {
      clearTimeout(resetMapTimersRef.current.timeout);
    }

    setDiagramCoverOpacity(0);
    setLoadingScreenProgress(0);

    const totalDuration = 1500; // Total timeout duration in ms
    const progressTimer = createLoadingProgressTimer(setLoadingScreenProgress, totalDuration);
    resetMapTimersRef.current.progressTimer = progressTimer;

    setIsInit(false);
    nodes = new Map();
    pipes = new Map();
    const newModel = new DiagramModel();
    runResetZoom();
    newModel.registerListener({
      eventDidFire: eventDidFireHandler,
    });
    engine.setModel(newModel);
    const timeout = setTimeout(() => {
      setDiagramCoverOpacity(1);
      clearInterval(progressTimer);
      setLoadingScreenProgress(100);
      resetMapTimersRef.current.progressTimer = undefined;
      resetMapTimersRef.current.timeout = undefined;
    }, totalDuration);
    resetMapTimersRef.current.timeout = timeout;
  };

  const initModel = () => {
    resetMap();
    const model = engine.getModel();
    devices.forEach(device => {
      const { deviceId, deviceType, name } = device;
      let node;
      let pipe;

      // abort node creation if not needed
      if (!needToCreateNode(device)) return;

      if (name?.includes(HIDDEN_COMPONENT_NAME) && deviceType !== DeviceType.Pipe) node = new DummyNodeModel(device);
      else
        switch (deviceType) {
          case DeviceType.FixedSpeed:
          case DeviceType.VariableSpeedDrive:
          case DeviceType.Centrifugal:
          case DeviceType.FixedSpeedAndPowerSensor:
          case DeviceType.VariableSpeedDriveAndPowerSensor:
          case DeviceType.CentrifugalAndPowerSensor:
            node = new CompressorNodeModel(device);
            break;

          case DeviceType.Connector:
            node = new DummyNodeModel(device);
            break;

          case DeviceType.Booster:
            node = new BoosterNodeModel(device);
            break;

          case DeviceType.WetAirTank:
            node = new AirTankNodeModel(NodeModelTypeEnum.WetAirTank, device);
            break;

          case DeviceType.DesiccantAirDryer:
            node = new DesiccantAirDryerNodeModel(device);
            break;

          case DeviceType.RefrigeratedAirDryer:
            node = new RefrigeratedAirDryerNodeModel(device);
            break;

          case DeviceType.FlowAndPressureSensor:
            node = new FlowAndPressureSensorNodeModel(device);
            break;

          case DeviceType.DewpointSensor:
            node = new DewpointSensorNodeModel(device);
            break;

          case DeviceType.DewpointAndPressureSensor:
            node = new DewpointAndPressureSensorNodeModel(device);
            break;

          case DeviceType.AirTemperatureSensor:
            node = new AirTemperatureSensorNodeModel(device);
            break;

          case DeviceType.AirFilter:
            node = new AirFilterNodeModel(device);
            break;

          case DeviceType.ProportionalValve:
          case DeviceType.OpenCloseValve:
          case DeviceType.SpillValve:
            node = new ValveNodeModel(device);
            break;

          case DeviceType.DryAirTank:
            node = new AirTankNodeModel(NodeModelTypeEnum.DryAirTank, device);
            break;

          case DeviceType.PowerSensor:
            break;

          case DeviceType.PressureSensor:
            node = new PressureSensorNodeModel(device);
            break;

          case DeviceType.WaterPressureSensor:
            node = new WaterPressureSensorNodeModel(device);
            break;

          case DeviceType.FlowSensor:
            node = new FlowSensorNodeModel(device);
            break;

          case DeviceType.ProductionFloor:
            node = new ProductionFloorNodeModel(device);
            break;

          case DeviceType.Pipe:
            pipe = new PipeLinkModel(device);
            break;

          case DeviceType.InfluentFlowSensor:
            node = new InfluentFlowSensorNodeModel(device);
            break;

          case DeviceType.DissolvedOxygenSensor:
            node = new DissolvedOxygenSensorNodeModel(device);
            break;

          case DeviceType.PhSensor:
            node = new PHSensorNodeModel(device);
            break;

          case DeviceType.AmmoniaAmmoniumSensor:
            node = new AmmoniaAmmoniumSensorNodeModel(device);
            break;

          case DeviceType.NitrateSensor:
            node = new NitrateSensorNodeModel(device);
            break;

          case DeviceType.NitriteSensor:
            node = new NitriteSensorNodeModel(device);
            break;

          case DeviceType.OxidationReductionPotentialSensor:
            node = new OxidationReductionPotentialSensorNodeModel(device);
            break;

          case DeviceType.Blower:
            node = new BlowerNodeModel(device);
            break;

          case DeviceType.AerationBasin:
            node = new AerationBasinNodeModel(device);
            break;

          default:
            break;
        }

      if (node) nodes.set(deviceId, node);
      if (pipe) pipes.set(deviceId, pipe);
    });

    connectSourceLinks();
    connectTargetLinks();

    for (const node of nodes.values()) {
      //node.setPosition(0, 0);
      model.addNode(node);
    }

    for (const pipe of pipes.values()) {
      pipe.color = token.colorBorder;
      model.addLink(pipe);
    }

    model.setLocked(true);
    engine.setModel(model);
    setIsInit(true);

    setTimeout(() => {
      dagreEngine.redistribute(model);
      const graphSize = engine.getBoundingNodesRect(engine.getModel().getNodes());
      const canvas = engine.getCanvas();
      if (!canvas) return;
      const screenSize = canvas.getBoundingClientRect();
      const screenRatio = screenSize.width / screenSize.height;
      const multiplier = Math.min(Math.max((graphSize.getHeight() * screenRatio) / graphSize.getWidth(), 0.5), 2);
      dagreEngine.options.graph.ranksep *= multiplier;
      dagreEngine.redistribute(model);
      updatePositions(engine);
      engine.repaintCanvas();
      runResetZoom();
      updateStoredZoomDebounced();
    }, 1000);
  };

  const updateModel = () => {
    const model = engine.getModel();
    const nodes = model.getNodes();
    let notFoundNodesCount = 0;
    devices.forEach(device => {
      const node = find(nodes, { device: { deviceId: device.deviceId } }) as ExtendedNodeModel;
      if (!node) {
        notFoundNodesCount += 1;
        return;
      }

      Object.assign(node.device, device);
    });
    if (notFoundNodesCount === devices.length) {
      resetMap();
    }
  };

  const addSourcePort = (node, pipe) => {
    if (!node || !pipe) return;
    const currentSourcePort = node.getPort(PortModelAlignment.RIGHT);
    const portSource = !currentSourcePort
      ? node.addPort(new FourPortPipeModel(PortModelAlignment.RIGHT))
      : currentSourcePort;
    pipe.setSourcePort(portSource);
  };

  const addTargetPort = (node, pipe) => {
    if (!node) return;
    const currentTargetPort = node.getPort(PortModelAlignment.LEFT);
    const portTarget = !currentTargetPort
      ? node.addPort(new FourPortPipeModel(PortModelAlignment.LEFT))
      : currentTargetPort;
    pipe.setTargetPort(portTarget);
  };

  const connectSourceLinks = () => {
    for (const node of nodes.values()) {
      const toDevicesArray = node.getEntity().toDevices as Array<string>;
      if (toDevicesArray !== null) {
        for (const id of toDevicesArray) {
          const pipe = pipes.get(id);
          if (pipe === null || pipe === undefined) {
            logger(['Pipe not found - ', id], { on: false, tags: ['yanai'] });
          }
          addSourcePort(node, pipe);
        }
      }
    }
  };

  const needToCreateNode = device => {
    let shouldCreateNode = false;
    const toDevicesArray = device.toDevices as Array<string>;

    if (
      deviceIsOfType(device.deviceType, [DeviceType.ProductionFloor, DeviceType.AerationBasin, DeviceType.DryAirTank])
    ) {
      shouldCreateNode = true;
    } else if (toDevicesArray !== null && toDevicesArray?.length > 0) {
      shouldCreateNode = true;
    }

    return shouldCreateNode;
  };

  const connectTargetLinks = () => {
    for (const pipe of pipes.values()) {
      logger(['a pipe', { pipe }], { on: false, tags: ['yanai'] });
      const toDevicesArray = pipe.getEntity().toDevices as Array<string>;

      if (toDevicesArray !== null) {
        for (const id of toDevicesArray) {
          const node = nodes.get(id);
          if (node === null || node === undefined) {
            logger(['Node not found - ', id], { on: false, tags: ['yanai'] });
          }
          addTargetPort(node, pipe);
        }
      }
    }
  };

  const resetZoom = () => {
    try {
      INITIAL_OFFSET_X = null;
      const model = engine.getModel();
      if (model) {
        model.clearSelection();
      }

      if (engine) {
        engine.zoomToFitNodes({ margin: DEFAULT_OFFSET });
        updateStoredZoomDebounced();
      }

      if (pidEditStore?.isInEditMode) {
        pidEditStore.hideMenu();
      }
    } catch (e) {
      logger('reset zoom error: ' + e, { on: true, tags: ['error'] });
    }
  };

  // hack to wait for the page to be in view
  const fixMapScale = () => {
    if (stationStore.isLoading) return;
    if (!windowIsOnFocus) return;

    const model = engine.getModel();
    const offsetX = model.getOffsetX();
    const offsetY = model.getOffsetY();
    if (!((offsetX === INITIAL_OFFSET_X && offsetY === INITIAL_OFFSET_Y) || nodes.size === 0)) return;

    setTimeout(() => {
      distribute(dagreEngine, engine);
      runResetZoom();
    });
  };

  const updateStoredZoom = () => {
    if (stationStore.isLoading) return;

    const model = engine.getModel();
    const offsetX = model.getOffsetX();
    const offsetY = model.getOffsetY();
    const zoom = model.getZoomLevel();

    if (offsetX === storeOffsetX || offsetY === storeOffsetY || zoom === storeZoom) return;
    if (offsetX === 0 && offsetY === 0 && zoom === 100) return;

    mapBuilderStore.setZoomData({
      offsetX,
      offsetY,
      zoom,
    });
  };

  const updateStoredZoomDebounced = useCallback(debounce(updateStoredZoom, 1000), []);

  const setStoredZoom = () => {
    const model = engine.getModel();
    model.setOffsetX(storeOffsetX);
    model.setOffsetY(storeOffsetY);
    model.setZoomLevel(storeZoom);
  };

  const runResetZoom = useCallback(() => {
    if (storeOffsetX === null || storeOffsetY === null || storeZoom === null) {
      resetZoom();
      return;
    }

    setStoredZoom();
  }, []);

  useEffect(() => {
    if (!isEmpty(previousToken) && !isEqual(previousToken, token)) {
      resetMap();
    }
  }, [token]);

  // initializing OR updating model upon change in devices array
  useEffect(() => {
    if (devices === undefined || devices === null) {
      resetMap();
      return;
    }

    // no need to init or update in case of 0 devices
    if (devices.length === 0) return;
    isInit ? updateModel() : initModel();

    if (pidEditStore.draftDeviceChanged) {
      pidEditStore.resetDraftDeviceChanged();
      resetZoom();
      resetMap();
    }
  }, [devices, pidEditStore.isInEditMode, pidEditStore.draftDeviceChanged]);

  // active fix if window is on focus
  useEffect(() => {
    if (!windowIsOnFocus) return;

    fixMapScale();
  }, [windowIsOnFocus]);

  useEffect(() => {
    if (stationStore.isLoading || pidEditStore.isLoading) return;

    setTimeout(() => {
      setDiagramCoverOpacity(1);
    }, 1000);
    fixMapScale();
  }, [stationStore.isLoading, pidEditStore.isLoading]);

  // update zoom
  useEffect(() => {
    if (storeOffsetX === null || storeOffsetY === null || storeZoom === null) return;

    const model = engine.getModel();
    const offsetX = model.getOffsetX();
    const offsetY = model.getOffsetY();
    const zoom = model.getZoomLevel();

    if (offsetX === storeOffsetX || offsetY === storeOffsetY || zoom === storeZoom) return;
  }, [mapBuilderStore.zoomData.offsetX, mapBuilderStore.zoomData.offsetY, mapBuilderStore.zoomData.zoom]);

  useEffect(() => {
    return () => {
      mapBuilderStore.closeMinimizedModal();
      // Clean up any remaining timers on unmount
      if (resetMapTimersRef.current.progressTimer) {
        clearInterval(resetMapTimersRef.current.progressTimer);
      }
      if (resetMapTimersRef.current.timeout) {
        clearTimeout(resetMapTimersRef.current.timeout);
      }
    };
  }, []);

  // Add effect to update edit mode
  useEffect(() => {
    const pipeLinkFactory = engine.getLinkFactories().getFactory('pipe') as PipeLinkFactory;
    if (pipeLinkFactory) {
      pipeLinkFactory.setEditMode(isEditMode);
    }
  }, [isEditMode, engine]);

  // fix for preventing map zooming when details modal is open
  useEffect(() => {
    if (mapBuilderStore.isAnyDetailsModalOpen || pidEditStore.isActiveMenuEnabled) {
      const zoomActions = engine.getActionEventBus().getActionsForType(InputType.MOUSE_WHEEL);
      zoomActions.forEach(action => engine.getActionEventBus().deregisterAction(action));
    } else engine.getActionEventBus().registerAction(new ZoomCanvasAction({ inverseZoom: true }));
  }, [mapBuilderStore.selectedDevice, mapBuilderStore.isDetailsModalMinimized, pidEditStore.isActiveMenuEnabled]);

  const [dagreEngine] = useState(
    new DagreEngine({
      graph: {
        rankdir: 'LR',
        //ranker: 'network-simplex',
        ranker: 'longest-path',
        nodesep: 30,
        ranksep: 80,
      },
      includeLinks: false,
    }),
  );

  const isMapLoading =
    stationStore.isLoading || stationStore.isLoadingDraftPid || pidEditStore.isLoading || pidEditStore.isLoadingDraft;

  const canViewEditMenu = () => {
    if (!permissionStore.isAuth(permissions.CAN_EDIT_MAP)) return false;

    const proactiveEngineState = proactiveEngineStore.proactiveEngineState;
    if (proactiveEngineState === ProactiveEngineStateEnum.Activated) return false;

    const roleName = userStore.getRoleNameByStationId(stationStore.currentStation.id);
    const currentStationStage = stationStore.currentStationStage?.stage?.name;

    if (roleName === 'Ecoplant Viewer') {
      return currentStationStage === 'Opportunity' || currentStationStage === 'Design' || currentStationStage === 'New';
    }
    return true;
  };

  const displayNoFlowControlNotificationPopup = () => {
    return stationStore.stationControlType === StationControlType.NoFlowControl;
  };

  const handleCloseNoFlowControlNotificationModal = () => {
    setIsNoFlowControlNotificationModalOpen(false);

    // Store in localStorage that the modal has been shown for this station
    if (stationStore.currentStation?.id) {
      markModalAsShown(stationStore.currentStation.id);
    }
  };

  return (
    <Container>
      {isMapLoading && (
        <LoadingOverlay>
          <SpinCentered text={t('Rendering Map...')} />
        </LoadingOverlay>
      )}
      <EditMapContainer>
        {isResetZoomVisible && <EPButton onClick={resetZoom}>{t('Reset zoom')}</EPButton>}
        {canViewEditMenu() && <EditModeMenu />}
      </EditMapContainer>
      <div className="diagram-cover" style={{ opacity: diagramCoverOpacity }}>
        <CanvasWidget className="diagram-container" engine={engine} />
        <PIDFullscreenEditor
          engine={engine}
          diagramCoverOpacity={diagramCoverOpacity}
          resetZoom={resetZoom}
          loadingScreenProgress={loadingScreenProgress}
        />
      </div>
      {displayNoFlowControlNotificationPopup() && (
        <NoFlowControlNotificationModal
          open={isNoFlowControlNotificationModalOpen}
          onClose={handleCloseNoFlowControlNotificationModal}
        />
      )}
    </Container>
  );
};

export default observer(MapBuilder);
